import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { layoutTree } from '../treeLayout'
import type { LayoutOptions } from '../treeLayout'
import { usePanZoom } from '../usePanZoom'
import { StructureCard } from './StructureCard'
import { ZoomControls } from './ZoomControls'
import { MOVE_KEYS, routeTo } from './visible'
import type { Move, Shown } from './visible'

/**
 * The node-link drawing: root on the left, what it is made of to the right,
 * on a canvas that drags, wheels, pinches and fits.
 *
 * **One transform moves everything.** Connectors are an SVG layer and cards are
 * DOM inside the same transformed box, so no pan can slide a line off its card.
 * Cards are DOM rather than SVG because they carry photographs, badges and two
 * real buttons each, and rebuilding focus, hover and tap targets in SVG is a
 * worse version of what the browser already does.
 *
 * Connectors are cubic curves leaving the parent's right edge and arriving
 * level at the child's left, which is the shape good diagram tools settled on:
 * a fan of curves reads as "these all belong to that", where elbows drawn at
 * the same x merge into one comb and hide which child hangs off which line.
 */
export const CARD: LayoutOptions = { cardW: 272, cardH: 94, colGap: 14, rowGap: 88, orientation: 'right' }


export function StructureDiagram({
  shown,
  resetKey,
  selected,
  stockKnown,
  rootNote,
  compact,
  imageFor,
  onSelect,
  onToggle,
  onMove,
}: {
  shown: Shown
  /** A new value opens the view afresh — a different root. */
  resetKey: string
  selected: string | null
  stockKnown: boolean
  rootNote: string | null
  compact: boolean
  imageFor: (typeId: string) => string | null
  onSelect: (key: string) => void
  onToggle: (key: string) => void
  /** Apply a keyboard move (opening or closing a branch if it is one); returns where focus goes. */
  onMove: (from: string, move: Move) => string
}): JSX.Element {
  const layout = useMemo(() => layoutTree<Shown>(shown, CARD), [shown])
  const first = layout.nodes[0]
  const pan = usePanZoom(layout, {
    resetKey,
    root: first === undefined ? undefined : { x: first.x, y: first.y, height: CARD.cardH },
  })
  /* A counter beside the key, so moving focus to the node it is already on
     (Up at the root) still runs the effect that puts it there. */
  const frame = useRef<HTMLDivElement | null>(null)
  const { frameRef } = pan
  const ref = useCallback(
    (el: HTMLDivElement | null) => {
      frame.current = el
      frameRef(el)
    },
    [frameRef],
  )
  const [focus, setFocus] = useState<{ readonly key: string; readonly n: number } | null>(null)

  /** The route from the root to the selection lights up. */
  const route = useMemo(() => routeTo(shown, selected), [shown, selected])

  const placedBy = useMemo(() => new Map(layout.nodes.map((p) => [p.node.key, p])), [layout])

  /* Focus follows the keyboard, and the camera follows focus — but only as far
     as it must (`revealBox`). Run after the render that applied the move, so a
     branch the move just opened is already in the DOM. */
  useEffect(() => {
    if (focus === null) return
    const at = placedBy.get(focus.key)
    if (at === undefined) return
    frame.current?.querySelector<HTMLButtonElement>(`.st-card[data-node="${CSS.escape(focus.key)}"]`)?.focus({ preventScroll: true })
    pan.reveal({ x: at.x - 24, y: at.y, width: CARD.cardW + 48, height: CARD.cardH })
    // `pan` is a fresh object each render; its `reveal` reads the frame directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, placedBy])

  /* A node selected from elsewhere — a shortage chip — is brought into view,
     clear of the inspector that is about to open over the right of the frame. */
  useEffect(() => {
    const at = selected === null ? undefined : placedBy.get(selected)
    if (at === undefined) return
    const inspector = window.matchMedia?.('(min-width: 900px)').matches ? 364 : 0
    pan.reveal({ x: at.x - 48, y: at.y, width: CARD.cardW + 72, height: CARD.cardH }, inspector)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, placedBy])

  const onFrameKey = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === '+' || e.key === '=') pan.zoomBy(1.25)
    else if (e.key === '-' || e.key === '_') pan.zoomBy(1 / 1.25)
    else if (e.key === '0') pan.fit()
    else return
    e.preventDefault()
  }

  return (
    <div
      className={`st-frame${compact ? ' st-frame--compact' : ''}${pan.panning ? ' st-frame--panning' : ''}`}
      ref={ref}
      onPointerDown={pan.onPointerDown}
      onPointerMove={pan.onPointerMove}
      onPointerUp={pan.onPointerUp}
      onPointerCancel={pan.onPointerUp}
      onKeyDown={onFrameKey}
    >
      <div
        className="st-canvas"
        style={{
          width: layout.width,
          height: layout.height,
          transform: `translate(${pan.view.x}px, ${pan.view.y}px) scale(${pan.view.scale})`,
        }}
      >
        <svg className="st-edges" width={layout.width} height={layout.height} aria-hidden="true">
          {layout.edges.map((e) => {
            const child = placedBy.get(e.key)?.node.node
            const bad = stockKnown && child !== undefined && (child.short > 0 || child.shortBelow > 0)
            const bend = (e.x2 - e.x1) * 0.55
            return (
              <path
                key={e.key}
                className={`${bad ? 'st-edge--short' : ''}${route.has(e.key) ? ' st-edge--route' : ''}`}
                d={`M${e.x1} ${e.y1} C${e.x1 + bend} ${e.y1} ${e.x2 - bend} ${e.y2} ${e.x2} ${e.y2}`}
              />
            )
          })}
        </svg>
        {layout.nodes.map((p) => (
          <div
            key={p.node.key}
            className="st-slot"
            style={{ left: p.x, top: p.y, width: CARD.cardW, height: CARD.cardH }}
          >
            <StructureCard
              shown={p.node}
              imageId={imageFor(p.node.node.typeId)}
              selected={selected === p.node.key}
              stockKnown={stockKnown}
              rootNote={p.node.node.depth === 0 ? rootNote : null}
              /* A pan that ends over a card is not a tap on it (`gesture.ts`). */
              onSelect={() => {
                if (pan.tapped()) onSelect(p.node.key)
              }}
              onToggle={() => {
                if (pan.tapped()) onToggle(p.node.key)
              }}
              onKeyDown={(e) => {
                const move = MOVE_KEYS[e.key]
                if (move === undefined) return
                e.preventDefault()
                const key = onMove(p.node.key, move)
                setFocus((f) => ({ key, n: (f?.n ?? 0) + 1 }))
              }}
            />
          </div>
        ))}
      </div>

      <ZoomControls scale={pan.view.scale} zoomBy={pan.zoomBy} fit={pan.fit} />
    </div>
  )
}
